import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";

import { useParams } from "react-router-dom";
import { ChangeEvent } from "../../shared/model/Event";
import { HashMap } from "../../shared/model/HashMap";
import { Option } from "../../shared/model/Option";
import { Utilities } from "../../shared/utilities";
import { getRules } from "../actions/AppActions";
import { AppContext } from "../App";
import { IAppReducerState, IProcessTriggerValue, IProcessTriggerValueOption, IPublicHoliday, IRuleData } from "../shared/model";
import { aggregates, expressionOperators, operators, timePeriodTypes, timeZonesAsOptions } from "../shared/ruleEditorMappings";

import RuleDeployment from "./editorSettingsComponents/RuleDeployment";
import GeneralSettings from "./editorSettingsComponents/GeneralSettings";
import TimeZoneDateSettings from "./editorSettingsComponents/TimeZoneDateSettings";
import { TabList, TabPanel, Tab } from "react-tabs";

const RuleEditor: React.FC<IRuleEditorProps> = (props) => {

    const { state, dispatch } = React.useContext<IAppReducerState>(AppContext);
    const { environmentName } = useParams<{ environmentName: string }>();
    const timeZones = timeZonesAsOptions(state?.timeZones);

    const initialFormData = {
        name: props?.rule?.name ?? null, // Required
        variableName: props?.rule?.config?.variableName ?? null, // Required
        processTriggerValues: !_.isEmpty(props?.rule?.config?.processTriggerValues) ? props?.rule?.config?.processTriggerValues : null,
        expressionOperator: props?.rule?.config?.expressionOperator ?? expressionOperators[0].value,
        expressionExpectedValue: props?.rule?.config?.expressionExpectedValue ?? null, // Required when expressionOperator is not Contains
        aggregate: props?.rule?.config?.aggregate ?? aggregates[0].value,
        operator: props?.rule?.config?.operator ?? operators[0].value,
        expectedValue: props?.rule?.config?.expectedValue ?? null, // Required
        timePeriod: props?.rule?.config?.timePeriod ?? null, // Required
        timePeriodType: props?.rule?.config?.timePeriodType ?? timePeriodTypes[0].value,
        timeZone: props?.rule?.config?.timeZone ?? timeZones?.find((x) => x.value === "FLE Standard Time")?.value,
        isActiveEveryDay: props?.rule?.config?.isActiveEveryDay ?? true,
        isActiveEntireDay: props?.rule?.config?.isActiveEntireDay ?? true,
        activeWeekDays: props?.rule?.config?.activeWeekDays ?? [], // Required when isActiveEveryDay is false
        startHour: props?.rule?.config?.startHour ?? null, // Required when isActiveEntireDay is false
        endHour: props?.rule?.config?.endHour ?? null, // Required when isActiveEntireDay is false,
        disablingHolidaysAreEnabled: props?.rule?.config?.disablingHolidaysAreEnabled ?? false,
        disablingHolidays: props?.rule?.config?.disablingHolidays ?? null // holidays that are already saved in the rule json
    };
    const [formData, setFormData] = React.useState<INewRuleFormData>(initialFormData);

    const [environment, setEnvironment] = React.useState<Option | null>(null);
    const [formDataErrors, setFormDataErrors] = React.useState<string[]>([]);
    const [defineFilterExpression, setDefineFilterExpression] = React.useState<boolean>(!_.isEmpty(props?.rule?.config?.expressionExpectedValue));
    const initialRecipients = props?.rule?.emailRecipients?.join(",");
    const [recipients, setRecipients] = React.useState<string | undefined>(initialRecipients);


    React.useEffect(() => {
        if (state.saveButtonClicked) {
            handleSaveRule();
        }
    }, [state.saveButtonClicked])

    React.useEffect(() => {
        if (!state.isDirty && (!_.isEqual(formData, initialFormData) || environment != null || recipients !== initialRecipients)) {
            dispatch({ type: "SET_IS_DIRTY", isDirty: true });
        }
    }, [formData, recipients, environment])

    const handleDefineFilterExpression = () => {
        if (defineFilterExpression) {
            setFormData({ ...formData, expressionOperator: expressionOperators[0].value, expressionExpectedValue: null });
        }
        setDefineFilterExpression(!defineFilterExpression);
    };

    const handleFormDataName = (e: ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, name: e.target.value });
    };

    const handleEnvironmentChange = (e: Option) => {
        setEnvironment(e ?? null);
    };

    const handleRecipients = (e: ChangeEvent<HTMLInputElement>) => {
        setRecipients(e.target.value);
    };

    const handleProcessTriggerValue = (triggerValues: IProcessTriggerValueOption[]) => {
        const mappedValues: IProcessTriggerValue[] = triggerValues?.map((triggerValue) => ({
            // Remove the Agent group identifier from processGuid
            // 8800a482-7372-4fac-b0b4-350a8cd7aac8_AgentGroupName -> 8800a482-7372-4fac-b0b4-350a8cd7aac8
            processGuid: triggerValue.value.substr(0, triggerValue.value.indexOf("_")),
            agentGroupName: triggerValue.agentGroupName
        }));
        setFormData({ ...formData, processTriggerValues: mappedValues });
    };

    const handleVariableToMonitor = (e: Option) => {
        setFormData({ ...formData, variableName: e.value });
    };

    const handleExpressionOperator = (e: Option) => {
        setFormData({ ...formData, expressionOperator: e.value });
    };

    const handleExpressionExpectedValue = (e: ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, expressionExpectedValue: e.target.value });
    };

    const handleAggregate = (e: Option) => {
        setFormData({ ...formData, aggregate: e.value });
    };

    const handleOperator = (e: Option) => {
        setFormData({ ...formData, operator: e.value });
    };

    const handleExpectedValue = (e: ChangeEvent<HTMLInputElement>) => {
        // tslint:disable-next-line: radix
        setFormData({ ...formData, expectedValue: parseInt(e.target.value) });
    };

    const handleTimePeriod = (e: ChangeEvent<HTMLInputElement> & Option) => {
        // This handler is used as HTMLInputElement and react-select, so we want to check for e.target, because react-select sends the value only as e
        if (e.target) {
            // tslint:disable-next-line: radix
            setFormData({ ...formData, timePeriod: parseInt(e.target.value) });
        } else {
            setFormData({ ...formData, timePeriod: e.value });
        }
    };

    const handleTimePeriodType = (e: Option) => {
        setFormData({ ...formData, timePeriod: null, timePeriodType: e.value });
    };

    const handleTimeZone = (e: Option) => {
        setFormData({ ...formData, timeZone: e.value });
    };

    const handleIsActiveEveryDay = () => {
        setFormData({ ...formData, isActiveEveryDay: true, activeWeekDays: [] });
    };
    const handleIsActiveOnSelectedDays = () => {
        setFormData({ ...formData, isActiveEveryDay: false });
    };

    const handleActiveWeekdays = (e: ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, activeWeekDays: _.xor(formData.activeWeekDays, [e.target.value]) });
    };

    const handleIsActiveEntireDay = () => {
        setFormData({ ...formData, isActiveEntireDay: true, startHour: null, endHour: null });
    };
    const handleIsActiveBetweenSelectedTimes = () => {
        setFormData({ ...formData, isActiveEntireDay: false });
    };

    const handleStartHour = (e: Option) => {
        // tslint:disable-next-line: radix
        setFormData({ ...formData, startHour: parseInt(e.value) });
    };
    const handleEndHour = (e: Option) => {
        // tslint:disable-next-line: radix
        setFormData({ ...formData, endHour: parseInt(e.value) });
    };
    const handleHasDisablingHolidaysEnabled = () => {
        setFormData({ ...formData, disablingHolidaysAreEnabled: true });
    }
    const handleHasDisablingHolidaysDisabled = () => {
        setFormData({ ...formData, disablingHolidaysAreEnabled: false });
    }
    const handleHolidaySelectionChange = (h: IPublicHoliday[]) => {
        setFormData({ ...formData, disablingHolidays: h });
    }

    const requiredFormDataFields = [
        "name",
        "variableName",
        "expectedValue",
        "timePeriod",
        formData.expressionOperator !== "Exists" ? "expressionExpectedValue" : null,
        !formData.isActiveEveryDay ? "activeWeekDays" : null,
        !formData.isActiveEntireDay ? "startHour" : null,
        !formData.isActiveEntireDay ? "endHour" : null
    ];

    // This checks our formData's keys with requiredFormDataFields
    const validateFields = (object: INewRuleFormData, keys: Array<string | null>) => {
        const localErrors: string[] = [];
        keys.forEach((k) => {
            if (k && k in object) {
                if ((object as HashMap<any>)[k] === null || (object as HashMap<any>)[k] === "null" || (_.isArray((object as HashMap<any>)[k]) && _.isEmpty((object as HashMap<any>)[k]))) {
                    console.log(k + " is empty");
                    setFormDataErrors((errors) => [...errors.filter((x) => x !== k), k]);
                    localErrors.push(...formDataErrors, k);
                    // We're not letting through hour values if they are over 24 or under 1
                } else if (k === "timePeriod" && formData.timePeriodType === "Hours" && ((object as HashMap<any>)[k] > 24 || (object as HashMap<any>)[k] < 1)) {
                    setFormDataErrors((errors) => [...errors.filter((x) => x !== k), k]);
                    localErrors.push(...formDataErrors, k);
                } else {
                    setFormDataErrors((errors) => errors.filter((x) => x !== k));
                    localErrors.filter((x) => x !== k);
                }
                return;
            }
        });
        return localErrors;
    };

    const handleSaveRule = async () => {
        const validate = () => validateFields(formData, requiredFormDataFields);
        dispatch({ type: "SET_IS_LOADING", isLoading: true });
        try {
            // We're not letting through specific formData fields that are empty
            if (_.isEmpty(validate())) {
                if (props.asNewRule) {
                    await handleAddNewRule();
                } else {
                    await handleSaveEditedRule();
                }
                Utilities.toastSuccess("Successfully saved rule.");
                handleHideRuleEditor();
                getRules(dispatch, environmentName);
            } else {
                Utilities.toastError("Rule is not valid");
            }
        } catch (e) {
            Utilities.toastError(e);
            console.error(e);
        }
        dispatch({ type: "SET_SAVE_BUTTON_CLICKED", saveButtonClicked: false });
        dispatch({ type: "SET_IS_LOADING", isLoading: false });
    }

    const handleAddNewRule = async () => {
        await Utilities.postJsonAndParseResponse("api/monitoringRules/newRule", {
            environmentName: environmentName,
            config: formData,
            emailRecipients: !_.isEmpty(recipients) ? recipients?.split(",").map((x) => x.trim()) : []
        });
    };

    const handleSaveEditedRule = async () => {
        // Nullify agentGroupDisplayName and processName as we dont need those values in db
        formData?.processTriggerValues?.forEach((processTriggerValue) => {
            processTriggerValue.agentGroupDisplayName = null;
            processTriggerValue.processName = null;
        });
        await Utilities.postJsonAndParseResponse("api/monitoringRules/editRule", {
            ruleId: props.rule?.id,
            config: formData,
            emailRecipients: !_.isEmpty(recipients) ? recipients?.split(",").map((x) => x.trim()) : []
        });
    };

    const handleHideRuleEditor = () => {
        dispatch({ type: "SET_IS_DIRTY", isDirty: false });
        if (props.asNewRule) {
            dispatch({ type: "SET_SHOW_NEW_RULE_EDITOR", showNewRuleEditor: false });
        } else if (props.asEditRule && props.onHideCallback) {
            props.onHideCallback();
        }
    };

    const renderGeneralSettingsErrors = () => {
        const generalErros = [
            "name",
            "variableName",
            "expectedValue",
            "timePeriod",
            "expressionExpectedValue"
        ];
        const errors = formDataErrors.filter((x) => generalErros.includes(x) && generalErros.map((e) => e === x)).length;
        return errors !== 0 && <S.SmallErrorChip style={{ marginLeft: 6 }} title="Errors" >{errors}</S.SmallErrorChip>;
    };

    const renderTimeZoneDateSettingsErrors = () => {
        const timeZoneDateErrors = [
            "activeWeekDays",
            "startHour",
            "endHour"
        ];
        const errors = formDataErrors.filter((x) => timeZoneDateErrors.includes(x) && timeZoneDateErrors.map((e) => e === x)).length;
        return errors !== 0 && <S.SmallErrorChip style={{ marginLeft: 6 }} title="Errors">{errors}</S.SmallErrorChip>;
    };

    const getGeneralSettings = () => {
        return <GeneralSettings
            rule={props.rule}
            asEditRule={props.asEditRule}
            asNewRule={props.asNewRule}
            formData={formData}
            formDataErrors={formDataErrors}
            recipients={recipients}
            defineFilterExpression={defineFilterExpression}
            handlers={{
                handleFormDataName: handleFormDataName,
                handleEnvironmentChange: handleEnvironmentChange,
                handleRecipients: handleRecipients,
                handleProcessTriggerValue: handleProcessTriggerValue,
                handleVariableToMonitor: handleVariableToMonitor,
                handleDefineFilterExpression: handleDefineFilterExpression,
                handleExpressionOperator: handleExpressionOperator,
                handleExpressionExpectedValue: handleExpressionExpectedValue,
                handleAggregate: handleAggregate,
                handleOperator: handleOperator,
                handleExpectedValue: handleExpectedValue,
                handleTimePeriod: handleTimePeriod,
                handleTimePeriodType: handleTimePeriodType
            }}
        />
    }

    const getTimeZoneDateSettings = () => {
        return <TimeZoneDateSettings
            formData={formData}
            formDataErrors={formDataErrors}
            handlers={{
                handleTimeZone: handleTimeZone,
                handleIsActiveEveryDay: handleIsActiveEveryDay,
                handleIsActiveOnSelectedDays: handleIsActiveOnSelectedDays,
                handleActiveWeekdays: handleActiveWeekdays,
                handleIsActiveEntireDay: handleIsActiveEntireDay,
                handleIsActiveBetweenSelectedTimes: handleIsActiveBetweenSelectedTimes,
                handleStartHour: handleStartHour,
                handleEndHour: handleEndHour,
                handleHasInactiveHolidaysEnabled: handleHasDisablingHolidaysEnabled,
                handleHasInactiveHolidaysDisabled: handleHasDisablingHolidaysDisabled,
                handleHolidaySelectionChange: handleHolidaySelectionChange
            }}
        />
    }
    return (
        <div>
            <form name="ruleForm">

                {props.asNewRule ?
                    <div>
                        <S.PanelHeading><h2>New rule</h2></S.PanelHeading>
                        <S.FormGroup>
                            <h3>General settings</h3>
                        </S.FormGroup>
                        <S.FormGroup>
                            {getGeneralSettings()}
                        </S.FormGroup>
                        <S.FormGroup>
                            <h3>Timezone & date settings</h3>
                        </S.FormGroup>
                        <S.FormGroup>
                            {getTimeZoneDateSettings()}
                        </S.FormGroup>
                    </div>
                    : <S.NonSolidTabs
                        id="RuleEditorTabs"
                        className="custom-tabs"
                        $tabListMargin="0 0 25px"
                    >

                        <TabList>
                            <Tab id="tab-general-settings">
                                General settings {renderGeneralSettingsErrors()}
                            </Tab>
                            <Tab id="tab-time-settings">
                                Timezone & date settings {renderTimeZoneDateSettingsErrors()}
                            </Tab>
                            {!props.asNewRule &&
                                <Tab id="tab-deploy-settings">
                                    {FRENDS.Localizations.Deploy} {FRENDS.Localizations.Rule.toLowerCase()}
                                </Tab>}
                        </TabList>
                        <TabPanel>
                            {getGeneralSettings()}
                        </TabPanel>
                        <TabPanel>
                            {getTimeZoneDateSettings()}
                        </TabPanel>
                        <TabPanel>
                            <RuleDeployment
                                rule={props.rule}
                            />
                        </TabPanel>
                    </S.NonSolidTabs>
                }
            </form >
        </div >
    );
};

export interface INewRuleFormData {
    name: string | null;
    variableName: string | null;
    processTriggerValues: IProcessTriggerValue[] | undefined | null;
    expressionOperator: string;
    expressionExpectedValue: string | null;
    aggregate: string;
    operator: string;
    expectedValue: number | null;
    timePeriodType: string;
    timePeriod: number | null;
    timeZone: string;
    isActiveEveryDay: boolean;
    isActiveEntireDay: boolean;
    activeWeekDays: string[];
    startHour: number | null;
    endHour: number | null;
    disablingHolidaysAreEnabled: boolean;
    disablingHolidays: IPublicHoliday[] | undefined | null;
}

export interface IRuleEditorProps {
    asNewRule?: boolean;
    asEditRule?: boolean;
    rule?: IRuleData;
    onHideCallback?: () => void;
}

export default RuleEditor;
